import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import produce from "immer";

import {
  IFoodItem,
  IFoodMenuCategory,
  ISubCategoriesJson,
  IMasterFoodMenu,
  IMenuJSON,
} from "../../../../redux/food/food.types";
import { IStoreState } from "../../../../redux/initialStoreState";
import { fetchFoodMenuCategoryListWithFoodItemsAsync } from "../../../../redux/food/foodActions";
import { useBillingCompanyContext } from "../../../../providers";
import { useDispatchWrapper } from "../../../../hooks";
import { LoadState } from "../../../../constants/enums";
import {
  isAtLimit,
  getCategoryItemCount,
  getSubcategoryItemCount,
} from "../utils/itemSelectionHelpers";
import { makeSelectionKey } from "../utils/selectionKey";

// ─── State Shape ────────────────────────────────────────────────────────────────

export interface IMenuBuilderState {
  // Mode
  mode: "master" | "lead";

  // Navigation
  activeCategory: IFoodMenuCategory | null;
  activeSubcategory: ISubCategoriesJson | null;
  categorySearchText: string;

  // Selection
  selectedItems: IFoodItem[];

  // Limits
  categoryLimits: Record<string, number>;

  // Visibility (master mode): itemUuid → isVisible
  itemVisibility: Record<string, boolean>;
  visibilityEditMode: boolean;

  // Master menu (lead mode)
  selectedMasterMenu: IMasterFoodMenu | null;

  // Form fields (master mode)
  menuTitle: string;
  menuPrice: number;

  // Loading/error
  categoriesLoading: boolean;
  categoriesError: string | null;

  // Mobile navigation
  activeTab: "categories" | "items" | "summary";

  // Category display order (lead mode) — array of category UUIDs
  categoryOrder: string[];
}

// ─── Actions Interface ──────────────────────────────────────────────────────────

export interface IMenuBuilderActions {
  selectCategory: (category: IFoodMenuCategory | null) => void;
  selectSubcategory: (
    category: IFoodMenuCategory,
    sub: ISubCategoriesJson | null
  ) => void;
  setCategorySearchText: (text: string) => void;
  addItem: (item: IFoodItem, subCategoryUuidOverride?: string | null) => void;
  removeItem: (selectionKey: string) => void;
  clearItems: () => void;
  setCategoryLimit: (id: string, limit: number) => void;
  loadMasterMenu: (menu: IMasterFoodMenu) => void;
  confirmMasterMenuSwitch: (menu: IMasterFoodMenu) => void;
  setMenuTitle: (title: string) => void;
  setMenuPrice: (price: number) => void;
  setActiveTab: (tab: "categories" | "items" | "summary") => void;
  retryLoadCategories: () => void;
  toggleVisibilityEditMode: () => void;
  toggleItemVisibility: (itemUuid: string) => void;
  isItemVisible: (itemUuid: string) => boolean;
  reorderCategory: (categoryId: string, direction: "up" | "down") => void;
  setCategoryOrder: (order: string[]) => void;
}

type MenuBuilderContextValue = IMenuBuilderState & IMenuBuilderActions;

// ─── Reducer Action Types ───────────────────────────────────────────────────────

type MenuBuilderAction =
  | { type: "SET_ACTIVE_CATEGORY"; payload: IFoodMenuCategory | null }
  | {
      type: "SET_ACTIVE_SUBCATEGORY";
      payload: {
        category: IFoodMenuCategory;
        sub: ISubCategoriesJson | null;
      };
    }
  | { type: "SET_CATEGORY_SEARCH_TEXT"; payload: string }
  | { type: "ADD_ITEM"; payload: IFoodItem }
  | { type: "REMOVE_ITEM"; payload: string }
  | { type: "CLEAR_ITEMS" }
  | { type: "SET_CATEGORY_LIMIT"; payload: { id: string; limit: number } }
  | { type: "LOAD_MASTER_MENU"; payload: IMasterFoodMenu }
  | { type: "CONFIRM_MASTER_MENU_SWITCH"; payload: IMasterFoodMenu }
  | { type: "SET_MENU_TITLE"; payload: string }
  | { type: "SET_MENU_PRICE"; payload: number }
  | { type: "SET_ACTIVE_TAB"; payload: "categories" | "items" | "summary" }
  | { type: "SET_CATEGORIES_LOADING"; payload: boolean }
  | { type: "SET_CATEGORIES_ERROR"; payload: string | null }
  | {
      type: "HYDRATE_FROM_MENU_DATA";
      payload: { items: IFoodItem[]; limits: Record<string, number>; visibility?: Record<string, boolean>; categoryOrder?: string[] };
    }
  | { type: "TOGGLE_VISIBILITY_EDIT_MODE"; payload?: { allItemUuids: string[] } }
  | { type: "TOGGLE_ITEM_VISIBILITY"; payload: string }
  | { type: "REORDER_CATEGORY"; payload: { categoryId: string; direction: "up" | "down" } }
  | { type: "SET_CATEGORY_ORDER"; payload: string[] };

// ─── Reducer ────────────────────────────────────────────────────────────────────

const menuBuilderReducer = produce(
  (draft: IMenuBuilderState, action: MenuBuilderAction) => {
    switch (action.type) {
      case "SET_ACTIVE_CATEGORY":
        draft.activeCategory = action.payload;
        draft.activeSubcategory = null;
        break;

      case "SET_ACTIVE_SUBCATEGORY":
        draft.activeCategory = action.payload.category;
        draft.activeSubcategory = action.payload.sub;
        break;

      case "SET_CATEGORY_SEARCH_TEXT":
        draft.categorySearchText = action.payload;
        break;

      case "ADD_ITEM": {
        // Stamp composite selection key: categoryUuid|subCategoryUuid|itemUuid
        const catUuid = action.payload.category_list?.[0]?.category_uuid ?? "";
        const subUuid = action.payload.sub_category_uuid ?? "";
        const itemUuid = action.payload.food_item_uuid ?? "";
        const selectionKey = makeSelectionKey(catUuid, subUuid, itemUuid);
        (action.payload as any)._selectionKey = selectionKey;
        draft.selectedItems.push(action.payload);
        // If category isn't in categoryOrder yet, append it
        const addedCatUuid = catUuid;
        if (addedCatUuid && draft.categoryOrder.length > 0 && !draft.categoryOrder.includes(addedCatUuid)) {
          draft.categoryOrder.push(addedCatUuid);
        }
        break;
      }

      case "REMOVE_ITEM":
        // payload is now a composite selectionKey, not a plain itemUuid
        draft.selectedItems = draft.selectedItems.filter(
          (item) => (item as any)._selectionKey !== action.payload
        );
        break;

      case "CLEAR_ITEMS":
        draft.selectedItems = [];
        break;

      case "SET_CATEGORY_LIMIT":
        draft.categoryLimits[action.payload.id] = action.payload.limit;
        break;

      case "LOAD_MASTER_MENU":
        draft.selectedMasterMenu = action.payload;
        hydrateMasterMenuIntoDraft(draft, action.payload);
        break;

      case "CONFIRM_MASTER_MENU_SWITCH":
        draft.selectedItems = [];
        draft.categoryLimits = {};
        draft.selectedMasterMenu = action.payload;
        hydrateMasterMenuIntoDraft(draft, action.payload);
        break;

      case "SET_MENU_TITLE":
        draft.menuTitle = action.payload;
        break;

      case "SET_MENU_PRICE":
        draft.menuPrice = action.payload;
        break;

      case "SET_ACTIVE_TAB":
        draft.activeTab = action.payload;
        break;

      case "SET_CATEGORIES_LOADING":
        draft.categoriesLoading = action.payload;
        break;

      case "SET_CATEGORIES_ERROR":
        draft.categoriesError = action.payload;
        break;

      case "HYDRATE_FROM_MENU_DATA": {
        // Deduplicate items by composite selectionKey (categoryUuid|subCategoryUuid|itemUuid)
        // so the same item can appear in multiple categories/subcategories independently
        const seenKeys = new Set<string>();
        const uniqueItems: IFoodItem[] = [];
        for (const item of action.payload.items) {
          const uuid = item.food_item_uuid ?? "";
          const catUuid = item.category_list?.[0]?.category_uuid ?? "";
          const subUuid = item.sub_category_uuid ?? "";
          const key = makeSelectionKey(catUuid, subUuid, uuid);
          if (uuid && !seenKeys.has(key)) {
            seenKeys.add(key);
            (item as any)._selectionKey = key;
            uniqueItems.push(item);
          }
        }
        draft.selectedItems = uniqueItems;
        draft.categoryLimits = action.payload.limits;
        if (action.payload.visibility) {
          draft.itemVisibility = action.payload.visibility;
        }
        if (action.payload.categoryOrder) {
          draft.categoryOrder = action.payload.categoryOrder;
        }
        break;
      }

      case "TOGGLE_VISIBILITY_EDIT_MODE":
        draft.visibilityEditMode = !draft.visibilityEditMode;
        // When enabling Fixed mode, mark all items as non-visible initially
        // BUT keep already-selected items as visible
        if (draft.visibilityEditMode && action.payload?.allItemUuids) {
          const hasExistingVisibility = Object.keys(draft.itemVisibility).length > 0;
          if (!hasExistingVisibility) {
            const selectedUuids = new Set(
              draft.selectedItems.map((item) => item.food_item_uuid ?? "")
            );
            for (const uuid of action.payload.allItemUuids) {
              // Selected items stay visible, rest become hidden
              draft.itemVisibility[uuid] = selectedUuids.has(uuid);
            }
          }
        }
        break;

      case "TOGGLE_ITEM_VISIBILITY": {
        const itemUuid = action.payload;
        const currentlyVisible = draft.itemVisibility[itemUuid] !== false;
        draft.itemVisibility[itemUuid] = !currentlyVisible;
        // If hiding an item that's selected, auto-deselect it
        if (currentlyVisible) {
          draft.selectedItems = draft.selectedItems.filter(
            (item) => item.food_item_uuid !== itemUuid
          );
        }
        break;
      }

      case "REORDER_CATEGORY": {
        const { categoryId, direction } = action.payload;
        const idx = draft.categoryOrder.indexOf(categoryId);
        if (idx === -1) break;
        const swapIdx = direction === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= draft.categoryOrder.length) break;
        // Swap
        const temp = draft.categoryOrder[idx];
        draft.categoryOrder[idx] = draft.categoryOrder[swapIdx];
        draft.categoryOrder[swapIdx] = temp;
        break;
      }

      case "SET_CATEGORY_ORDER": {
        draft.categoryOrder = action.payload;
        break;
      }

      default:
        break;
    }
  }
);

// ─── Helper: Hydrate master menu data into draft ────────────────────────────────

function hydrateMasterMenuIntoDraft(
  draft: IMenuBuilderState,
  menu: IMasterFoodMenu
): void {
  const items: IFoodItem[] = [];
  const limits: Record<string, number> = {};
  const visibility: Record<string, boolean> = {};
  const seenKeys = new Set<string>();

  for (const category of menu.menu_json) {
    // Category-level limit
    if (category.max_selection && category.max_selection > 0) {
      limits[category.category_uuid] = category.max_selection;
    }

    // Category-level food items
    if (category.food_items && category.food_items.length > 0) {
      for (const item of category.food_items) {
        const itemUuid = item.food_item_uuid ?? "";
        const subUuid = item.sub_category_uuid ?? "";
        const selectionKey = makeSelectionKey(category.category_uuid, subUuid, itemUuid);
        if (!itemUuid || seenKeys.has(selectionKey)) continue;
        seenKeys.add(selectionKey);

        const hydratedItem: IFoodItem = {
          ...item,
          category_list: [
            {
              category_uuid: category.category_uuid,
              category_name: category.category_name,
              category_description: "",
              company_outlet_uuid: "",
              status: "ACTIVE",
              food_items: [],
              subcategories: [],
            },
          ],
        };
        (hydratedItem as any)._selectionKey = selectionKey;
        // Read visibility
        if ((item as any).isVisible === false) {
          visibility[itemUuid] = false;
        } else {
          visibility[itemUuid] = true;
        }
        // Only add to selectedItems if isSelected (or undefined for backward compat)
        const isItemSelected = (item as any).isSelected !== undefined
          ? (item as any).isSelected === true
          : true;
        if (isItemSelected) {
          items.push(hydratedItem);
        }
      }
    }

    // Subcategory items and limits
    if (category.subcategories) {
      for (const sub of category.subcategories) {
        if (sub.max_selection && sub.max_selection > 0) {
          limits[sub.food_subcategory_name_uuid ?? ""] = sub.max_selection;
        }

        if (sub.food_items && sub.food_items.length > 0) {
          for (const item of sub.food_items) {
            const itemUuid = item.food_item_uuid ?? "";
            const subUuid = sub.food_subcategory_name_uuid ?? "";
            const selectionKey = makeSelectionKey(category.category_uuid, subUuid, itemUuid);
            if (!itemUuid || seenKeys.has(selectionKey)) continue;
            seenKeys.add(selectionKey);

            const hydratedItem: IFoodItem = {
              ...item,
              sub_category_uuid: sub.food_subcategory_name_uuid,
              category_list: [
                {
                  category_uuid: category.category_uuid,
                  category_name: category.category_name,
                  category_description: "",
                  company_outlet_uuid: "",
                  status: "ACTIVE",
                  food_items: [],
                  subcategories: [],
                },
              ],
            };
            (hydratedItem as any)._selectionKey = selectionKey;
            // Read visibility
            if ((item as any).isVisible === false) {
              visibility[itemUuid] = false;
            } else {
              visibility[itemUuid] = true;
            }
            // Only add to selectedItems if isSelected
            const isItemSelected = (item as any).isSelected !== undefined
              ? (item as any).isSelected === true
              : true;
            if (isItemSelected) {
              items.push(hydratedItem);
            }
          }
        }
      }
    }
  }

  draft.selectedItems = items;
  draft.itemVisibility = { ...draft.itemVisibility, ...visibility };
  // Set visibilityEditMode if master menu is fixed
  if (menu.is_fixed === true || menu.is_fixed === 1) {
    draft.visibilityEditMode = true;
  }
  draft.categoryLimits = { ...draft.categoryLimits, ...limits };
}

// ─── Helper: Find subcategory UUID for an item from menu_json data ──────────────

/**
 * Determines which subcategory an item belongs to by checking:
 * 1. The item's category_list[0].subcategories (which contains food_items arrays from the API)
 * 2. The category's subcategories in menu_json
 * 3. Fallback: check against full Redux categories data
 */
function findSubcategoryUuidForItem(
  item: IFoodItem,
  category: IMenuJSON,
  allCategories?: IFoodMenuCategory[]
): string | null {
  // Strategy 1: Check item's own category_list for subcategories with matching food_items
  if (item.category_list && item.category_list.length > 0) {
    const catData = item.category_list[0];
    if (catData.subcategories && catData.subcategories.length > 0) {
      for (const sub of catData.subcategories) {
        if (sub.food_items?.some((fi) => fi.food_item_uuid === item.food_item_uuid)) {
          return sub.food_subcategory_name_uuid;
        }
      }
    }
  }

  // Strategy 2: Check the category's subcategories in menu_json
  if (category.subcategories) {
    for (const sub of category.subcategories) {
      if (sub.food_items?.some((fi) => fi.food_item_uuid === item.food_item_uuid)) {
        return sub.food_subcategory_name_uuid;
      }
    }
  }

  // Strategy 3: Check against full Redux category data
  if (allCategories) {
    const matchedCategory = allCategories.find(
      (cat) => cat.category_uuid === category.category_uuid
    );
    if (matchedCategory?.subcategories) {
      for (const sub of matchedCategory.subcategories) {
        if (sub.food_items?.some((fi) => fi.food_item_uuid === item.food_item_uuid)) {
          return sub.food_subcategory_name_uuid;
        }
      }
    }
  }

  return null;
}

// ─── Helper: Find subcategory UUID for an item ─────────────────────────────────

function findSubcategoryForItem(
  item: IFoodItem,
  activeCategory: IFoodMenuCategory | null
): string | null {
  if (!activeCategory || !activeCategory.subcategories) return null;

  for (const sub of activeCategory.subcategories) {
    if (sub.food_items?.some((fi) => fi.food_item_uuid === item.food_item_uuid)) {
      return sub.food_subcategory_name_uuid;
    }
  }
  return null;
}

/**
 * Search all Redux categories to find which subcategory an item belongs to.
 */
function findSubcategoryInReduxCategories(
  item: IFoodItem,
  allCategories: IFoodMenuCategory[]
): string | null {
  for (const category of allCategories) {
    if (!category.subcategories) continue;
    for (const sub of category.subcategories) {
      if (sub.food_items?.some((fi) => fi.food_item_uuid === item.food_item_uuid)) {
        return sub.food_subcategory_name_uuid;
      }
    }
  }
  return null;
}

// ─── Provider Props ─────────────────────────────────────────────────────────────

export interface IMenuBuilderProviderProps {
  mode: "master" | "lead";
  initialMenuData?: IMenuJSON[];
  initialLimits?: Record<string, number>;
  initialPrice?: number;
  initialIsFixed?: boolean | number;
  masterMenuUuid?: string;
  title?: string;
  children: React.ReactNode;
}

// ─── Context ────────────────────────────────────────────────────────────────────

const MenuBuilderContext = createContext<MenuBuilderContextValue | null>(null);

// ─── Provider Component ─────────────────────────────────────────────────────────

export const MenuBuilderProvider: React.FC<IMenuBuilderProviderProps> = ({
  mode,
  initialMenuData,
  initialLimits,
  initialPrice,
  initialIsFixed,
  masterMenuUuid,
  title,
  children,
}) => {
  const dispatch = useDispatchWrapper();
  const { comapny } = useBillingCompanyContext();
  const companyOutletUuid = comapny?.company_outlet_uuid ?? "";

  // Redux selectors for server data
  const categoriesLoadState = useSelector(
    (state: IStoreState) => state.food.foodCategory.loading
  );
  const categoriesFromRedux = useSelector(
    (state: IStoreState) => state.food.foodCategory.data
  );
  const masterMenusFromRedux = useSelector(
    (state: IStoreState) => state.food.foodMenu.data
  );

  // ─── Initial State ──────────────────────────────────────────────────────────

  const initialState: IMenuBuilderState = {
    mode,
    activeCategory: null,
    activeSubcategory: null,
    categorySearchText: "",
    selectedItems: [],
    categoryLimits: initialLimits ?? {},
    itemVisibility: {},
    visibilityEditMode: initialIsFixed === true || initialIsFixed === 1,
    selectedMasterMenu: null,
    menuTitle: title ?? "",
    menuPrice: initialPrice ?? 0,
    categoriesLoading: true,
    categoriesError: null,
    activeTab: "categories",
    categoryOrder: [],
  };

  const [state, reducerDispatch] = useReducer(menuBuilderReducer, initialState);

  // ─── Sync Redux loading state ───────────────────────────────────────────────

  useEffect(() => {
    const isLoading = categoriesLoadState === LoadState.InProgress;
    const hasFailed = categoriesLoadState === LoadState.Failed;

    reducerDispatch({
      type: "SET_CATEGORIES_LOADING",
      payload: isLoading,
    });

    if (hasFailed) {
      reducerDispatch({
        type: "SET_CATEGORIES_ERROR",
        payload: "Failed to load categories. Please try again.",
      });
    } else if (categoriesLoadState === LoadState.Loaded) {
      reducerDispatch({
        type: "SET_CATEGORIES_ERROR",
        payload: null,
      });
    }
  }, [categoriesLoadState]);

  // ─── Fetch categories on mount ─────────────────────────────────────────────

  const fetchCategories = useCallback(() => {
    if (!companyOutletUuid) return;

    reducerDispatch({ type: "SET_CATEGORIES_LOADING", payload: true });
    reducerDispatch({ type: "SET_CATEGORIES_ERROR", payload: null });

    dispatch(
      fetchFoodMenuCategoryListWithFoodItemsAsync({
        company_outlet_uuid: companyOutletUuid,
        // page: 1,
        // rowsPerPage: -1, // fetch all
        // pageLimit: -1,
        status: "ACTIVE",
      })
    );
  }, [companyOutletUuid, dispatch]);

  useEffect(() => {
    fetchCategories(); 
  }, [fetchCategories]);

  // ─── Hydrate from initialMenuData ──────────────────────────────────────────

  const hydratedRef = React.useRef(false);

  useEffect(() => {
    if (!initialMenuData || initialMenuData.length === 0) return;
    if (hydratedRef.current) return; // Only hydrate once

    hydratedRef.current = true;

    // Also hydrate title and price if provided
    if (title) {
      reducerDispatch({ type: "SET_MENU_TITLE", payload: title });
    }
    if (initialPrice != null && initialPrice > 0) {
      reducerDispatch({ type: "SET_MENU_PRICE", payload: initialPrice });
    }

    const items: IFoodItem[] = [];
    const limits: Record<string, number> = { ...(initialLimits ?? {}) };
    const visibility: Record<string, boolean> = {};
    const addedItemKeys = new Set<string>();

    for (const category of initialMenuData) {
      if (category.max_selection && category.max_selection > 0) {
        limits[category.category_uuid] = category.max_selection;
      }

      // Extract subcategory limits
      if (category.subcategories) {
        for (const sub of category.subcategories) {
          if (sub.max_selection && sub.max_selection > 0) {
            limits[sub.food_subcategory_name_uuid ?? ""] = sub.max_selection;
          }
        }
      }

      // Items are stored at category level in menu_json
      if (category.food_items && category.food_items.length > 0) {
        for (const item of category.food_items) {
          const itemUuid = item.food_item_uuid ?? "";
          if (!itemUuid) continue;

          // Use item's own sub_category_uuid first, then try to find it
          const subCatUuid = item.sub_category_uuid || findSubcategoryUuidForItem(item, category, categoriesFromRedux);

          // Composite key: same item can exist in multiple category/sub slots
          const selectionKey = makeSelectionKey(
            category.category_uuid,
            subCatUuid,
            itemUuid,
          );
          if (addedItemKeys.has(selectionKey)) continue;
          addedItemKeys.add(selectionKey);

          // Read visibility flag
          if ((item as any).isVisible === false) {
            visibility[itemUuid] = false;
          } else {
            visibility[itemUuid] = true;
          }

          // Only add to selectedItems if item was actually selected
          // For fixed menus: isSelected flag indicates selection
          // For non-fixed menus: all items in food_items are selected (backward compat)
          const isItemSelected = (item as any).isSelected !== undefined
            ? (item as any).isSelected === true
            : true; // backward compat: no isSelected means it was selected

          if (isItemSelected) {
            const hydratedItem: IFoodItem = {
              ...item,
              sub_category_uuid: subCatUuid || null,
              category_list: item.category_list?.length
                ? item.category_list
                : [
                    {
                      category_uuid: category.category_uuid,
                      category_name: category.category_name,
                      category_description: "",
                      company_outlet_uuid: "",
                      status: "ACTIVE",
                      food_items: [],
                      subcategories: [],
                    },
                  ],
            };
            (hydratedItem as any)._selectionKey = selectionKey;
            items.push(hydratedItem);
          }
        }
      }

      // NOTE: We only read from category.food_items.
      // subcategories[x].food_items is redundant (same items duplicated there by buildSavePayload)
      // and we use findSubcategoryUuidForItem to determine sub_category_uuid instead.
    }

    // Extract category order from the menu_json array order
    const categoryOrder = initialMenuData.map((cat) => cat.category_uuid);

    reducerDispatch({
      type: "HYDRATE_FROM_MENU_DATA",
      payload: { items, limits, visibility, categoryOrder },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMenuData]);

  // ─── Hydrate from masterMenuUuid (lead mode) ──────────────────────────────
  // Only used when NO initialMenuData is provided (consumer handles its own data)

  useEffect(() => {
    if (mode !== "lead" || !masterMenuUuid) return;
    // Skip if initialMenuData was provided — consumer already passed the data
    if (initialMenuData && initialMenuData.length > 0) return;

    // Look for the master menu in Redux store
    const matchedMenu = masterMenusFromRedux.find(
      (m) => m.master_menu_uuid === masterMenuUuid
    );

    if (matchedMenu) {
      reducerDispatch({ type: "LOAD_MASTER_MENU", payload: matchedMenu });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterMenuUuid, masterMenusFromRedux, mode]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const selectCategory = useCallback(
    (category: IFoodMenuCategory | null) => {
      reducerDispatch({ type: "SET_ACTIVE_CATEGORY", payload: category });
    },
    []
  );

  const selectSubcategory = useCallback(
    (category: IFoodMenuCategory, sub: ISubCategoriesJson | null) => {
      reducerDispatch({
        type: "SET_ACTIVE_SUBCATEGORY",
        payload: { category, sub },
      });
    },
    []
  );

  const setCategorySearchText = useCallback((text: string) => {
    reducerDispatch({ type: "SET_CATEGORY_SEARCH_TEXT", payload: text });
  }, []);

  const addItem = useCallback(
    (item: IFoodItem, subCategoryUuidOverride?: string | null) => {
      // Always anchor the item to the category the user is currently viewing,
      // NOT the item's DB-level category_list (which can span multiple categories).
      const activeCat = state.activeCategory;
      const category_list = activeCat ? [activeCat] : item.category_list ?? [];

      // Sub-category placement priority:
      // 1. Explicit override passed from the clicked chip's section
      // 2. Active subcategory in UI context
      // 3. Resolve within the active category only (do NOT fall back to a
      //    global Redux scan — that reintroduces cross-category bleed)
      const sub_category_uuid =
        subCategoryUuidOverride !== undefined
          ? subCategoryUuidOverride
          : state.activeSubcategory?.food_subcategory_name_uuid ??
            findSubcategoryForItem(item, state.activeCategory) ??
            null;

      const finalItem: IFoodItem = {
        ...item,
        category_list,
        sub_category_uuid,
      };

      reducerDispatch({ type: "ADD_ITEM", payload: finalItem });
    },
    [state.activeCategory, state.activeSubcategory]
  );

  const removeItem = useCallback((selectionKey: string) => {
    // selectionKey is a composite "categoryUuid|subCategoryUuid|itemUuid" — NOT a plain itemUuid
    reducerDispatch({ type: "REMOVE_ITEM", payload: selectionKey });
  }, []);

  const clearItems = useCallback(() => {
    reducerDispatch({ type: "CLEAR_ITEMS" });
  }, []);

  const setCategoryLimit = useCallback((id: string, limit: number) => {
    reducerDispatch({ type: "SET_CATEGORY_LIMIT", payload: { id, limit } });
  }, []);

  const loadMasterMenu = useCallback((menu: IMasterFoodMenu) => {
    reducerDispatch({ type: "LOAD_MASTER_MENU", payload: menu });
  }, []);

  const confirmMasterMenuSwitch = useCallback((menu: IMasterFoodMenu) => {
    reducerDispatch({ type: "CONFIRM_MASTER_MENU_SWITCH", payload: menu });
  }, []);

  const setMenuTitle = useCallback((newTitle: string) => {
    reducerDispatch({ type: "SET_MENU_TITLE", payload: newTitle });
  }, []);

  const setMenuPrice = useCallback((price: number) => {
    reducerDispatch({ type: "SET_MENU_PRICE", payload: price });
  }, []);

  const setActiveTab = useCallback(
    (tab: "categories" | "items" | "summary") => {
      reducerDispatch({ type: "SET_ACTIVE_TAB", payload: tab });
    },
    []
  );

  const retryLoadCategories = useCallback(() => {
    fetchCategories();
  }, [fetchCategories]);

  const toggleVisibilityEditMode = useCallback(() => {
    // Collect all item UUIDs from Redux categories
    const allItemUuids: string[] = [];
    for (const category of categoriesFromRedux) {
      if (category.food_items) {
        for (const item of category.food_items) {
          if (item.food_item_uuid) allItemUuids.push(item.food_item_uuid);
        }
      }
      if (category.subcategories) {
        for (const sub of category.subcategories) {
          if (sub.food_items) {
            for (const item of sub.food_items) {
              if (item.food_item_uuid) allItemUuids.push(item.food_item_uuid);
            }
          }
        }
      }
    }
    reducerDispatch({ type: "TOGGLE_VISIBILITY_EDIT_MODE", payload: { allItemUuids } });
  }, [categoriesFromRedux]);

  const toggleItemVisibility = useCallback((itemUuid: string) => {
    reducerDispatch({ type: "TOGGLE_ITEM_VISIBILITY", payload: itemUuid });
  }, []);

  const isItemVisible = useCallback((itemUuid: string) => {
    return state.itemVisibility[itemUuid] !== false;
  }, [state.itemVisibility]);

  const reorderCategory = useCallback((categoryId: string, direction: "up" | "down") => {
    reducerDispatch({ type: "REORDER_CATEGORY", payload: { categoryId, direction } });
  }, []);

  const setCategoryOrder = useCallback((order: string[]) => {
    reducerDispatch({ type: "SET_CATEGORY_ORDER", payload: order });
  }, []);

  // ─── Context Value ──────────────────────────────────────────────────────────

  const contextValue: MenuBuilderContextValue = {
    ...state,
    selectCategory,
    selectSubcategory,
    setCategorySearchText,
    addItem,
    removeItem,
    clearItems,
    setCategoryLimit,
    loadMasterMenu,
    confirmMasterMenuSwitch,
    setMenuTitle,
    setMenuPrice,
    setActiveTab,
    retryLoadCategories,
    toggleVisibilityEditMode,
    toggleItemVisibility,
    isItemVisible,
    reorderCategory,
    setCategoryOrder,
  };

  return (
    <MenuBuilderContext.Provider value={contextValue}>
      {children}
    </MenuBuilderContext.Provider>
  );
};

// ─── Internal Hook ──────────────────────────────────────────────────────────────

/**
 * Internal hook for accessing MenuBuilder context.
 * NOT exported from the barrel — only used by internal FoodMenuBuilder sub-components.
 */
export const useMenuBuilder = (): MenuBuilderContextValue => {
  const context = useContext(MenuBuilderContext);
  if (!context) {
    throw new Error(
      "useMenuBuilder must be used within a MenuBuilderProvider"
    );
  }
  return context;
};
